import { useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import axios from "axios";
import useApi from "../../hooks/useApi";
import SessionService from "../../services/SessionService";
import { useBranding } from "../../branding/BrandingProvider";
import AuthPageFrame, { AUTH_LINK_COLOR } from "./AuthPageFrame";
import { authErrorMessage } from "./authErrorMessage";

/**
 * Where a full page load goes. A plain object (not a bare function) so a test can spy on it: jsdom
 * cannot navigate, and the redirect has to be a FULL load — a signed-in browser is rendering the
 * authenticated route tree, and clearing the session in storage does not swap trees on its own.
 */
export const secureAccountNavigation = {
  toForgotPassword: () => window.location.assign("/forgot-password"),
};

type Phase = "confirm" | "working" | "secured" | "failed";

/**
 * "Wasn't you? Secure my account" — the target of the button in the new-device sign-in email.
 *
 * Nothing happens on page load. The user has to press the button, because mail scanners open every
 * link in an email (and some run its script) — an auto-submitting page would sign the owner out of
 * everything the moment the email arrived. After the API confirms, every session is gone, so the
 * local session marker is cleared too and the user is sent to reset their password.
 */
const SecureAccount = () => {
  const brand = useBranding();
  const { token } = useParams<{ token: string }>();
  const api = useApi(false);
  const [phase, setPhase] = useState<Phase>("confirm");
  const [error, setError] = useState<string>("");

  const secure = async () => {
    setPhase("working");
    try {
      const response = await api.post(
        "/auth/secure-account",
        { token },
        // This screen reports its own outcome; the bare 429 from the rate limiter has no body.
        { suppressErrorDialog: true },
      );
      if (response.data?.success) {
        SessionService.getInstance().clearSession();
        setPhase("secured");
        return;
      }
      setError(
        authErrorMessage(
          response.status,
          response.data,
          "We couldn't secure your account with this link.",
        ),
      );
      setPhase("failed");
    } catch (e) {
      const response = axios.isAxiosError(e) ? e.response : undefined;
      setError(
        authErrorMessage(
          response?.status ?? 0,
          response?.data,
          "We couldn't secure your account with this link.",
        ),
      );
      setPhase("failed");
    }
  };

  return (
    <AuthPageFrame eyebrow="Account security" title="Secure your account">
      {(phase === "confirm" || phase === "working") && (
        <>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            If you didn't just sign in to {brand.displayName}, we'll sign your
            account out on every device and browser, then help you choose a new
            password.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={secure}
              disabled={phase === "working" || !token}
            >
              {phase === "working" ? "Securing..." : "Sign me out everywhere"}
            </Button>
          </Box>
        </>
      )}

      {phase === "secured" && (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            You've been signed out on every device.
          </Alert>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Now choose a new password, so whoever signed in can't do it again.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => secureAccountNavigation.toForgotPassword()}
            >
              Reset my password
            </Button>
          </Box>
        </>
      )}

      {phase === "failed" && (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            If you're worried someone else has signed in to your account, reset
            your password now.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => secureAccountNavigation.toForgotPassword()}
            >
              Reset my password
            </Button>
          </Box>
        </>
      )}

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <RouterLink to="/login" style={{ textDecoration: "none" }}>
          <Typography
            variant="body2"
            sx={{
              color: AUTH_LINK_COLOR,
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Back to sign in
          </Typography>
        </RouterLink>
      </Box>
    </AuthPageFrame>
  );
};

export default SecureAccount;
